import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { FormattedMessage } from 'react-intl';

import * as collectionActions from '../../actions/collection';
import * as analyticsActions from '../../actions/analytics';

import {
  selectAlbumCollection,
  selectCollectionIds,
  selectNextAlbumCollectionCursor,
} from '../../selectors/collection';

import { selectFeatureFlag } from '../../selectors/features';

import stylesMediaObjectGrid from '../util/MediaObjectGrid.css';

import dataComponent from '../../hoc/dataComponent';

import EmptyCollection from './EmptyCollection';
import CollectionArchiveHeader from './CollectionArchiveHeader';
import LimitedCollection from './LimitedCollection';
import PaginatedCollection from './PaginatedCollection';
import CollectionItem from './CollectionItem';

import { createGetLimitedCollection, createGetCollectionArchive } from './PlaylistCollection.js';

import CollectionAlbums from '../../components/messages/CollectionAlbums';

import { ENTITY_TYPE_ALBUM, ENTITY_TYPE_ALBUMS } from '../../constants';
import protectedComponent from '../../hoc/protectedComponent';
import shareableEntity, { shareableEntityPropTypes } from '../../hoc/shareableEntity.js';

class AlbumCollection extends Component {
  static propTypes = {
    albumCollection: PropTypes.array,
    limitedCollection: PropTypes.array,
    albumIds: PropTypes.array,
    loadAlbumCollection: PropTypes.func.isRequired,

    analyticsTrack: PropTypes.func.isRequired,
    trackPlayPausePressed: PropTypes.func.isRequired,

    nextCursor: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,

    collectionAccessLimit: PropTypes.number,
    hasDisabledCollectionItems: PropTypes.bool.isRequired,

    ...shareableEntityPropTypes,
  };

  onItemClick = ({ content }) => {
    this.props.analyticsTrack('Selected Album From Collection', {
      id: content.id,
    });
  };

  renderAlbumDisplayItem = album => {
    return (
      <CollectionItem
        collectionItem={album}
        entityType={ENTITY_TYPE_ALBUM}
        onItemClick={this.onItemClick}
        beforeTogglePlay={this.beforeTogglePlay}
        key={album.id}
      />
    );
  };

  renderLimitedCollectionInfo = () => {
    return (
      <React.Fragment>
        <h1 className="fz--beta">
          <FormattedMessage
            id="collection.albums.limited-collection-albums"
            defaultMessage="Albums"
          />
        </h1>
        <p className="fz--delta">
          <FormattedMessage
            id="collection.albums.limit"
            defaultMessage="You can save up to { count } albums for free."
            values={{ count: this.props.collectionAccessLimit }}
          />
        </p>
      </React.Fragment>
    );
  };

  renderLimitedCollection() {
    return (
      <LimitedCollection
        listClassName={stylesMediaObjectGrid.list}
        limitedCollection={this.props.limitedCollection}
        renderItem={this.renderAlbumDisplayItem}
        renderInfo={this.renderLimitedCollectionInfo}
      />
    );
  }

  renderArchiveInfo() {
    return (
      <CollectionArchiveHeader>
        <FormattedMessage
          id="collection.albums.previously-saved"
          defaultMessage="Your Previously Saved Albums"
        />
      </CollectionArchiveHeader>
    );
  }

  renderPaginatedCollection() {
    const { albumCollection, isLoading } = this.props;

    return (
      <PaginatedCollection
        loadMore={this.loadMore}
        hasMore={!!this.props.nextCursor}
        items={albumCollection}
        renderItem={this.renderAlbumDisplayItem}
        isLoading={isLoading}
        title={<CollectionAlbums />}
        showShareModal={this.props.showShareModal}
      />
    );
  }

  renderEmptyAlbumCollection() {
    return (
      <EmptyCollection entityType={ENTITY_TYPE_ALBUM} showShareModal={this.props.showShareModal} />
    );
  }

  render() {
    const { albumIds, collectionAccessLimit, hasDisabledCollectionItems } = this.props;
    const hasAlbums = albumIds.length !== 0;
    const shouldRenderPaginatedCollection =
      hasAlbums && (hasDisabledCollectionItems || !collectionAccessLimit);
    return (
      <div>
        {!hasAlbums && this.renderEmptyAlbumCollection()}
        {collectionAccessLimit && this.renderLimitedCollection()}
        {hasDisabledCollectionItems && this.renderArchiveInfo()}
        {shouldRenderPaginatedCollection && this.renderPaginatedCollection()}
      </div>
    );
  }

  loadMore = () => {
    this.props.loadAlbumCollection(this.props.nextCursor);
  };

  beforeTogglePlay = (item, playing) => {
    this.props.trackPlayPausePressed({ contextType: 'favouriteAlbums' }, playing);
  };
}

const getLimitedCollection = createGetLimitedCollection();
const getCollectionArchive = createGetCollectionArchive();

function mapStateToProps(state) {
  const collectionAccessLimit = selectFeatureFlag(state, 'collections').access_limit;
  const albumCollection = selectAlbumCollection(state);
  const hasDisabledCollectionItems =
    collectionAccessLimit && collectionAccessLimit < albumCollection.length;

  return {
    isLoading: state.lists.collection.albums.loading,
    albumIds: selectCollectionIds(state).entities.albumIds,
    limitedCollection:
      collectionAccessLimit && getLimitedCollection(albumCollection, collectionAccessLimit),
    albumCollection: hasDisabledCollectionItems
      ? getCollectionArchive(albumCollection, collectionAccessLimit)
      : albumCollection,
    nextCursor: selectNextAlbumCollectionCursor(state),
    collectionAccessLimit,
    hasDisabledCollectionItems,
  };
}

function fetchData(store) {
  return store.dispatch(collectionActions.loadAlbumCollection());
}

function getShareTrackingContext() {
  return {
    sharedContentType: 'Album',
    sharedContentId: null,
    contextType: 'favouriteAlbums',
    contentId: null,
  };
}

export default compose(
  protectedComponent({ authenticatedOnly: true }),
  dataComponent(fetchData),
  connect(mapStateToProps, {
    loadAlbumCollection: collectionActions.loadAlbumCollection,
    trackPlayPausePressed: analyticsActions.trackPlayPausePressed,
    analyticsTrack: analyticsActions.track,
  }),
  shareableEntity(ENTITY_TYPE_ALBUMS, getShareTrackingContext)
)(AlbumCollection);
